import React, { useEffect, useRef } from 'react';

const buttonClass = 'rounded-md px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

const BeefreeLeaveDialog = ({ open, busy = false, canSave = true, error, onStay, onDiscard, onSave }) => {
  const dialogRef = useRef(null);
  const stayRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const dialog = dialogRef.current;
    const previous = document.activeElement;
    const returnTarget = previous?.matches?.('button, input, textarea, select, a[href]') ? previous : null;
    // Native dialog restores its previous focus on close. Never let that be
    // the SDK iframe: entering it is our conservative Free-plan edit signal.
    if (previous?.tagName === 'IFRAME') previous.blur();
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    stayRef.current?.focus({ preventScroll: true });
    return () => {
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
      if (returnTarget?.isConnected && !returnTarget.disabled) returnTarget.focus({ preventScroll: true });
    };
  }, [open]);

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby='beefree-leave-title'
      aria-describedby='beefree-leave-description'
      aria-modal='true'
      aria-busy={busy}
      className='max-w-lg rounded-xl border-0 bg-white p-0 text-slate-900 shadow-2xl backdrop:bg-slate-900/40'
      style={{ width: 'calc(100% - 2rem)', maxHeight: 'calc(100vh - 2rem)' }}
      onCancel={(event) => {
        event.preventDefault();
        if (!busy) onStay();
      }}
    >
      <div className='p-5'>
        <div className='flex items-center gap-2'>
          <svg className='flex-shrink-0 text-amber-600' aria-hidden='true' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.75' strokeLinecap='round' strokeLinejoin='round'>
            <path d='M10.3 3.9 1.9 18.4A2 2 0 0 0 3.6 21h16.8a2 2 0 0 0 1.7-2.6L13.7 3.9a2 2 0 0 0-3.4 0Z' /><path d='M12 9v4m0 4h.01' />
          </svg>
          <h2 id='beefree-leave-title' className='text-lg font-semibold'>Leave this email?</h2>
        </div>
        <p id='beefree-leave-description' className='mt-2 text-sm leading-5 text-slate-600'>Your latest changes may not be saved. Save before leaving to keep your work.</p>
        {error && <p role='alert' className='mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800'>{error}</p>}
        {busy && <p role='status' className='mt-4 text-sm text-slate-600'>Saving your email. Please wait…</p>}
        <div className='mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end'>
          <button ref={stayRef} type='button' disabled={busy} onClick={onStay} className={`${buttonClass} border border-slate-300 bg-white text-slate-700 hover:bg-slate-50`}>Keep editing</button>
          <button type='button' disabled={busy} onClick={onDiscard} className={`${buttonClass} text-red-600 hover:bg-red-50`}>Leave without saving</button>
          <button type='button' disabled={busy || !canSave} onClick={onSave} className={`${buttonClass} bg-blue-600 text-white hover:bg-blue-700`}>{busy ? 'Saving…' : 'Save and leave'}</button>
        </div>
      </div>
    </dialog>
  );
};

export default BeefreeLeaveDialog;
